/* pendingInvite — session-scoped storage for a PROPHETS FRIEND
 * invitation code the visitor arrived with.
 *
 * Behaviour matches the Phase-1 spec + the Feb-2026 persistence fix:
 *   • Saved on `/hub/invite/:code` first mount.
 *   • Survives client-side navigation, Hub page refreshes, and the
 *     Discord OAuth redirect (backend also persists it via an HttpOnly
 *     cookie so the two independently guarantee arrival).
 *   • Included on EVERY "Connect Discord" CTA anywhere in the Hub via
 *     `discordLoginUrl()`.
 *   • Cleared ONLY when the invitation is:
 *       - successfully accepted (post-OAuth `/hub?auth=ok` landing, or
 *         a successful `POST /hub/invites/redeem`), OR
 *       - confirmed invalid / expired by the backend (preview 404 or a
 *         `RedemptionError` from `/hub/invites/redeem`).
 *   • Never cleared on plain route changes.
 *
 * Kept intentionally free of imports so it can also be exercised by a
 * lightweight jsdom test harness.
 */
const KEY = "hub_pending_invite";

function safeSession() {
  // sessionStorage may be unavailable in old Safari private mode /
  // some embedded webviews; degrade to no-op.
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function setPendingInvite(code) {
  const s = safeSession();
  const clean = (code || "").trim();
  if (!s || !clean) return;
  try { s.setItem(KEY, clean); } catch { /* ignore quota errors */ }
}

export function getPendingInvite() {
  const s = safeSession();
  if (!s) return "";
  try { return (s.getItem(KEY) || "").trim(); } catch { return ""; }
}

export function clearPendingInvite() {
  const s = safeSession();
  if (!s) return;
  try { s.removeItem(KEY); } catch { /* ignore */ }
}

/** Build the Discord OAuth login URL for the current visitor, adding
 *  a `?invite=` query param when a pending invitation is stored so the
 *  backend can auto-redeem it after the callback.
 *
 *  Pass B — `return_to` is an optional same-origin Hub path that the
 *  backend persists through the OAuth round-trip. Whitelisted server-
 *  side to same-origin `/hub/*` paths so it can never be turned into
 *  an open redirect. Used by the Shareable Raffle deep-links so a
 *  logged-out visitor lands back on the exact same raffle after
 *  admission. */
export function discordLoginUrl(returnTo) {
  const base = `${process.env.REACT_APP_BACKEND_URL}/api/hub/auth/discord/login`;
  const invite = getPendingInvite();
  const params = new URLSearchParams();
  if (invite) params.set("invite", invite);
  if (typeof returnTo === "string" && returnTo.startsWith("/hub/")) {
    params.set("return_to", returnTo);
  }
  const q = params.toString();
  return q ? `${base}?${q}` : base;
}
